/**
 * DIE KACHELEBENE DER KARTE - 1:1 aus den alten Unterlagen.
 *
 * ORIGINALTEXT MARVIN (04.09.2026)
 *   „ohne witz, die qualität ist immernoch schlecht und es ist immernich nicht
 *    so wie es war. deine aufgabe ist jetzt das alte system, scripte
 *    auszulesen und dann alles 1:1 in der neuen plattform einzubinden mit den
 *    funktionen zu dem auftrag vorbereiten und tiefbauaufwand planen! das muss
 *    jetzt final laufen!!!"
 *
 * **NACHGESEHEN, UND DIE ENTSCHEIDENDE ZEILE STEHT IN `ebene()`:**
 *
 * ```js
 * // Bei doppelter Punktdichte eine Stufe TIEFER holen und halb so gross
 * // zeichnen: doppelt so viel Bild auf derselben Flaeche.
 * var wunsch = ZOOM + (DPR >= 1.5 ? 1 : 0);
 * var z = Math.min(wunsch, maxz), f = Math.pow(2, ZOOM-z);
 * var gr = KACHEL*f;
 * ```
 *
 * Der alte Dialog holt auf einem Bildschirm mit doppelter Punktdichte die
 * FEINERE Stufe und zeichnet jede Kachel halb so gross. Auf demselben
 * Ausschnitt liegen damit VIER MAL so viele Bildpunkte. Dazu sind seine
 * Zeichenflächen `BREIT*DPR` breit und tragen `setTransform(DPR,0,0,DPR,0,0)`:
 * sie rechnen in Anzeigepunkten und speichern in echten Bildpunkten.
 *
 * **DIESE PLATTFORM HAT DIE KACHELN BIS .104 ALS `<img>` MIT 256 PIXELN KANTE
 * IN DIE SEITE GESTELLT.** Ein `<img>` hat keine Punktdichte: auf einem
 * heutigen Bildschirm sind das halb so viele Bildpunkte je Zentimeter wie im
 * alten Dialog, und auf einem 4K-Schirm ein Viertel. Kein Prüfstand konnte das
 * sehen - die Kachel ist da, sie ist vollständig, sie ist richtig gerechnet.
 * **Sie ist nur halb so scharf.** Und .103 hat mit „eine Stufe gröber" noch
 * eine halbe Stufe daraufgelegt.
 *
 * Die reinen Regeln stehen deshalb HIER und nicht in der Komponente daneben:
 * eine Datei mit JSX lässt sich mit `--experimental-strip-types` nicht laden,
 * und **eine Textsuche misst, dass etwas dasteht, nicht, dass es stimmt.**
 * Fünfte Datei dieser Art nach `namen.ts`, `terminraster.ts`, `klebeband.ts`,
 * `kartenmass.ts` und `oberflaeche.ts`.
 */

export const KACHEL = 256;

export interface Punkt { lat: number; lng: number; }

/**
 * Web Mercator, Weltpunkte einer Stufe. Wörtlich `welt()` des alten Dialogs.
 */
export function nachPixel(p: Punkt, zoom: number): { x: number; y: number } {
  const n = KACHEL * 2 ** zoom;
  const s = Math.sin((p.lat * Math.PI) / 180);
  return {
    x: ((p.lng + 180) / 360) * n,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n,
  };
}

/** Und zurück. Wörtlich `ausWelt()` des alten Dialogs. */
export function ausPixel(x: number, y: number, zoom: number): Punkt {
  const n = KACHEL * 2 ** zoom;
  const k = Math.PI - (2 * Math.PI * y) / n;
  return {
    lng: (x / n) * 360 - 180,
    lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(k) - Math.exp(-k))),
  };
}

/** Meter je ANZEIGEPUNKT auf dieser Stufe und dieser Breite. */
export function meterJePixel(lat: number, zoom: number): number {
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
}

/**
 * DIE PUNKTDICHTE WIRD GEDECKELT, und zwar nach oben UND nach unten.
 *
 * Wörtlich der alte Dialog:
 * `DPR = Math.max(1, Math.min(2, window.devicePixelRatio || 1))`.
 *
 * Nach unten, weil ein Wert unter eins die Zeichenfläche kleiner machte als
 * die Anzeige - dann wäre das Bild gröber als der Bildschirm. Nach oben, weil
 * jede Stufe mehr die Zahl der Kacheln vervierfacht: auf einem Schirm mit
 * dreifacher Dichte holte das Fenster neun Mal so viele Kacheln von einem
 * fremden Dienst, und der steht seit .100 in einer Schlange von drei.
 */
export const DPR_HOECHSTENS = 2;

/**
 * **UND NACH UNTEN GILT AB .114 NICHT DER BILDSCHIRM, SONDERN ZWEI.**
 *
 * ORIGINALTEXT MARVIN (05.09.2026, mit dem Vergleich zur Vorgängerlösung)
 *   „bei den luftbildern finde ich das bild wieder unschäfer ... auch ist die
 *    beschriftung der hausnummer und der straßen nicht sauber, schau mal bei
 *    der reinen karte, da ist das um ein vielfaches klarer ... alles deutlich
 *    schärfer von der qualität!!"
 *
 * **DER UNTERSCHIED IST NICHT DER DIENST, SONDERN DIE ABTASTUNG.** Die alte
 * Lösung hat eine Karte gezeigt, die der BROWSER zeichnet: dort ist eine
 * Hausnummer eine Schrift und wird beim Zeichnen so fein gerastert, wie der
 * Bildschirm kann. Hier kommt sie als BILD von einem Landesamt, und bis .113
 * wurde dieses Bild in genau so vielen Bildpunkten bestellt, wie die Anzeige
 * breit ist: ein Buchstabe von acht Punkten Höhe hat dann acht Punkte, und
 * jede Kante liegt da, wo das Raster sie hinlässt.
 *
 * **BESTELLT WIRD JETZT IMMER DOPPELT SO FEIN UND HALB SO GROSS GEZEICHNET.**
 * Vier Messwerte je Anzeigepunkt statt einem - dieselbe Rechnung, die der
 * alte Dialog auf einem Schirm mit doppelter Dichte gemacht hat, nur eben
 * nicht mehr vom Bildschirm abhängig. Der Gewinn liegt bei allem, was der
 * Dienst RECHNET (Schrift, Linien, Grenzen); ein Luftbild wird davon nicht
 * schärfer als seine Quelle, aber auch nicht unschärfer.
 *
 * **BEZAHLT WIRD ES MIT VIER MAL SO VIELEN BILDPUNKTEN**, und deshalb bleibt
 * der Deckel: `WMS_KANTE_HOECHSTENS` fängt die grossen Bildschirme ab, und
 * die Karte holt je Lage EIN Bild und nicht vierzig Kacheln (seit .106).
 */
export const UEBERABTASTUNG = 2;

export function punktdichte(dpr: number): number {
  const d = Number(dpr);
  if (!Number.isFinite(d)) return UEBERABTASTUNG;
  return Math.max(UEBERABTASTUNG, Math.min(DPR_HOECHSTENS, d));
}

/**
 * WELCHE STUFE GEWÜNSCHT WIRD - eine feinere bei doppelter Punktdichte.
 *
 * Die Schwelle ist 1,5 und nicht 2: ein Bildschirm mit 1,5-facher Dichte
 * (viele Windows-Geräte stehen auf 150 %) profitiert genauso, und die halbe
 * Stufe dazwischen gibt es nicht.
 */
export const DICHTE_SCHWELLE = 1.5;

export function wunschstufe(zoom: number, dpr: number): number {
  return zoom + (punktdichte(dpr) >= DICHTE_SCHWELLE ? 1 : 0);
}

/* DIE KACHELRECHNUNG STEHT SEIT .106 AUF DEM SERVER.
 *
 * ORIGINALTEXT MARVIN (04.09.2026)
 *   „die zweite meldung friert nun alles ein, es hängt wieder. ich kann
 *    nichts klicken."
 *
 * Hier standen `kachelplan`, `istLeerAusPunkten` und `stufeSenken` - die
 * Rechnung, mit der das Fenster VIERZIG Kacheln je Lage geholt und die
 * feinste Stufe des Dienstes selbst gesucht hat. Sie ist nicht weggefallen,
 * sondern UMGEZOGEN: nach `api/src/regeln/kartenbild.ts` und
 * `api/src/kachelbild.ts`. **Der Server fragt den Dienst; das Fenster hat es
 * aus einem Kopf erfahren und danach alles noch einmal geholt.**
 *
 * Was hier bleibt, ist die Projektion (jede Zeichnung hängt daran), die
 * Punktdichte (die Fläche wird im Browser aufgespannt) und die Grösse einer
 * Grube im Gelände.
 */

/**
 * WIE VIELE ECHTE BILDPUNKTE EIN KERN VON `kante` ANZEIGEPUNKTEN HAT.
 *
 * Wörtlich `punkteLesen()` des alten Dialogs:
 * `var s = Math.max(3, Math.round(kante*DPR));`
 *
 * **`getImageData` kennt die Skalierung nicht - es liest immer echte
 * Bildpunkte.** Wer den Kern nicht mitskaliert, tastet auf einem Schirm mit
 * doppelter Dichte eine halb so grosse Fläche ab, und aus fünf mal fünf
 * Punkten werden fünf mal fünf VIERTEL eines Punktes: dann trifft die Probe
 * wieder die Fuge zwischen zwei Pflastersteinen, gegen die sie gebaut ist.
 */
export function kernkante(kante: number, dpr: number): number {
  return Math.max(3, Math.round(kante * punktdichte(dpr)));
}

/* EINE MARKIERUNG HAT EINE GRÖSSE IM GELÄNDE, KEINE IN BILDPUNKTEN.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „auch das markieren der grube ist ein problem, denn der ring ist immer in
 *    der gleichen größe und passt sich dem zoom nicht an"
 *
 * Der Ring stand mit `r={13}` fest. Auf Stufe 21 deckte er einen halben Meter
 * ab, auf Stufe 15 den halben Vorgarten - und damit sagte er auf keiner Stufe
 * dasselbe. Eine Montagegrube ist rund anderthalb Meter breit; genau das wird
 * gezeichnet, und der Ring wächst beim Zoomen mit.
 *
 * DER DECKEL NACH UNTEN IST KEINE ZIER: unter sieben Bildpunkten trifft ihn
 * kein Daumen mehr, und die Markierung ist zum Anfassen da. Nach oben, damit
 * eine weit herausgezoomte Karte nicht unter einem einzigen Fleck verschwindet.
 *
 * DIESELBE RECHNUNG STEHT IM DRUCK (`api/src/regeln/geo.ts`,
 * `grubenRadiusPixel`), sonst zeigt das Papier an die Gemeinde einen anderen
 * Kreis als der Bildschirm, an dem er gesetzt wurde. Prüfstand 073 legt beide
 * nebeneinander.
 */
export const GRUBE_METER = 1.5;
export const GRUBE_MIN_PX = 7;
export const GRUBE_MAX_PX = 60;

export function grubenRadius(mpp: number): number {
  if (!Number.isFinite(mpp) || mpp <= 0) return GRUBE_MIN_PX;
  return Math.max(GRUBE_MIN_PX, Math.min(GRUBE_MAX_PX, GRUBE_METER / 2 / mpp));
}

/**
 * .583: WIE HOCH DIE KARTE SEIN DARF, OHNE DASS DIE SEITE ROLLT.
 *
 * Marvin, 02.11.2026: „du siehst auf der Karte, ist immer noch ein
 * Scrollbalken am Ende. Für was brauchen wir nicht?" Bis hier zog die Karte
 * die Fensterhöhe minus 56 Punkte ab, gemessen am Fenster. Die Seite rollt
 * aber in der Bühne, und darunter stehen noch der Hinweis und der Rand der
 * Inhaltsfläche. Jetzt wird gegen die Bühne gemessen: ihr unterer Rand minus
 * der obere der Karte minus alles, was unter der Karte noch kommt.
 */
export function kartenHoehe(rahmen: HTMLElement, mindestens = 380): number {
  const buehne = rahmen.closest('.buehne') as HTMLElement | null;
  const unten = buehne ? buehne.getBoundingClientRect().bottom : window.innerHeight;
  const oben = rahmen.getBoundingClientRect().top;
  // Was unter der Karte steht: der Hinweis (Geschwister nach dem Kartenrahmen) und das Polster der Inhaltsfläche.
  let danach = 0;
  const karte = rahmen.firstElementChild as HTMLElement | null;
  if (karte) { for (let n = karte.nextElementSibling as HTMLElement | null; n; n = n.nextElementSibling as HTMLElement | null) danach += n.getBoundingClientRect().height + 8; }
  const inhalt = rahmen.closest('.inhalt') as HTMLElement | null;
  const polster = inhalt ? parseFloat(getComputedStyle(inhalt).paddingBottom || '0') || 0 : 0;
  return Math.max(mindestens, Math.floor(unten - oben - danach - polster - 4));
}
